import SubscriptionCancelPopup from "__utilities/components/SubscriptionCancelPopup";
import SubscriptionUpgradePopup from "__utilities/components/SubscriptionUpgradePopup";
import {
  isSubscriptionEnding,
  isSubscriptionRenewing,
  paymentActionUrl,
} from "__utilities/functions/subscription";

/**
 * Three cases, derived from the cancel-at-period-end flag rather than the status:
 *
 *  - renewing  -> cancel only, for now. "Skift til X betaling" and "Skift abonnement" are
 *                 both hidden here: the former always reaches ChangeSubscriptionType's
 *                 Month<->Year path, which is refused unconditionally as a stopgap (see
 *                 stripe-migration-plan.md, "Further Stripe improvements") and would error
 *                 100% of the time with only two plan types; the latter opens a popup whose
 *                 Premium button is itself already disabled while renewing
 *                 (SubscriptionUpgradePopup's isButtonDisabled), so it has nothing useful to
 *                 do here either. Restore "Skift til X betaling" once ChangePrice is rebuilt
 *                 on a Stripe Subscription Schedule.
 *  - ending    -> resume (they are still Premium until the period runs out, so there is
 *                 nothing to cancel and no need to re-enter a card)
 *  - otherwise -> subscribe again
 *
 * "Cancelled" is no longer a status, so the old status comparisons could never match.
 */
export default function AccountViewButtons({
  userInfo,
  refreshUserInfo,
  setOverlay,
  unsubscribePayment,
  resumeSubscription,
}) {
  const hideOverlay = () => setOverlay({ show: false, content: <></> });

  const showUpgradePopup = () => {
    setOverlay({
      show: true,
      content: (
        <SubscriptionUpgradePopup
          hideOverlay={hideOverlay}
          userInfo={userInfo}
          refreshUser={() => refreshUserInfo()}
        />
      ),
    });
  };

  // Which buttons apply is decided by whether a subscription is live, not by the licence.
  // A live subscription can coexist with an expired licence -- a first payment not recorded
  // yet, or a card the provider is still retrying -- and offering "subscribe again" there
  // would only be refused by the backend.
  if (isSubscriptionRenewing(userInfo)) {
    const actionUrl = paymentActionUrl(userInfo);

    return (
      <div className="flex flex-row w-full h-2/5 justify-center items-center border-t border-lightGrey p-5 gap-x-5">
        {actionUrl && (
          <div
            className="flex flex-row w-1/2 justify-center items-center bg-primaryBlue text-white font-latoBold text-standardLarge p-5 border border-lightGrey cursor-pointer uppercase"
            onClick={() => window.location.assign(actionUrl)}
          >
            Gennemfør betaling
          </div>
        )}
        <div
          className="flex flex-row w-1/2 justify-center items-center bg-white text-primaryBlue font-latoBold text-standardLarge p-5 border border-lightGrey cursor-pointer uppercase"
          onClick={() => {
            setOverlay({
              show: true,
              content: (
                <SubscriptionCancelPopup
                  userInfo={userInfo}
                  keepSubscription={hideOverlay}
                  confirmCancellation={() => {
                    // The status stays Active and access continues; only the flag changes,
                    // so re-read user info rather than waiting for a status change.
                    unsubscribePayment(() => {
                      refreshUserInfo();
                    });
                    hideOverlay();
                  }}
                />
              ),
            });
          }}
        >
          Opsig abonnement
        </div>
      </div>
    );
  }

  if (isSubscriptionEnding(userInfo)) {
    return (
      <div className="flex flex-row w-full h-2/5 justify-center items-center border-t border-lightGrey p-5 gap-x-5">
        <div
          className="flex flex-row w-1/2 justify-center items-center bg-primaryBlue text-white font-latoBold text-standardLarge p-5 border border-lightGrey cursor-pointer uppercase"
          onClick={() => resumeSubscription()}
        >
          Genoptag abonnement
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full h-2/5 justify-center items-center border-t border-lightGrey p-5 gap-x-5">
      <div
        className="flex flex-row w-1/2 justify-center items-center bg-primaryBlue text-white font-latoBold text-standardLarge p-5 border border-lightGrey cursor-pointer uppercase"
        onClick={showUpgradePopup}
      >
        Forny abonnement
      </div>
    </div>
  );
}
